import { watch } from 'vue';
import type { UnleashClient } from '@unleash/proxy-client-vue';
import { buildUnleashContext } from '@cutr/constants/feature-flags';
import { useActiveUserStore } from '@/stores/user';
import { liveUpdates } from '@/modules/live-updates';
import { queryClient } from '@/queries/queryClient';
import i18next from '@/helpers/i18n';

export function startSessionSync(unleashClient: UnleashClient) {
  const userStore = useActiveUserStore();

  userStore.$subscribe((mutation, { user }) => {
    localStorage.setItem(mutation.storeId, JSON.stringify({ user }));
  });

  watch(
    () => userStore.isLoggedIn,
    (isLoggedIn) => {
      if (isLoggedIn) {
        liveUpdates.connect();
      } else {
        liveUpdates.disconnect();
      }
    },
    { immediate: true }
  );

  // Cached responses belong to the session that fetched them:
  // clear cache on session change.
  watch(
    () => [
      userStore.activeUserId,
      userStore.activeSessionType,
      userStore.activeSessionRole,
    ],
    (session, previous) => {
      if (previous && session.every((part, i) => part === previous[i])) return;
      queryClient.resetQueries();
      queryClient.getMutationCache().clear();
    }
  );

  watch(
    () => userStore.user,
    (user) => {
      unleashClient.updateContext(
        buildUnleashContext({
          userEmail: user?.email,
          sessionId: user?.sessionId,
          devHostname: import.meta.env.VITE_DEV_HOSTNAME,
          userAgent: navigator.userAgent,
          userLang: i18next.resolvedLanguage,
          sessionType: user?.sessionType,
        })
      );
    },
    { immediate: true }
  );
}
